import {
  Accordion,
  Button,
  Group,
  HoverCard,
  NumberInput,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  IconArrowDown,
  IconCalendarTime,
  IconClock,
  IconHelp,
  IconInfoCircle,
  IconSendOff,
  IconSettings,
  IconShare,
  IconWiper,
} from "@tabler/icons-react";
import SharingController from "../../../../../components/Sharing/SharingController";
import CampaignSharer from "./CampaignSharer";
import CampaignAdvancedSettings from "./CampaignAdvancedSettings";
import { DateTimePicker, TimeInput } from "@mantine/dates";
import { useFlowStore } from "../../../../../stores/WorkflowStore";
import { useForm } from "react-hook-form";
import { defaultProgramData } from "../flow/flowSettings";
import CampaignExclusion from "../exclusion/CampaignExclusion";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getObjectWithChangedProperties } from "../../../../../functions/utilFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { editCampaign } from "../../../../../api/StatsAPI";
import { programWorkflow } from "../../../../../api/WorkflowAPI";
import { notifications } from "@mantine/notifications";
import { useLeadsStore } from "../../../../../stores/LeadsStore";
import { createSharing, editSharing } from "../../../../../api/PRMAPI";
import { useListStore } from "../../../../../stores/ListStore";
import { modals } from "@mantine/modals";
import ProfileTimezone from "../../../../../components/View/ProfileTimezone";
import dayjs from "dayjs";
import { useProfileStore } from "../../../../../stores/UserStore";

const ProgrammationStep = ({ setActive }) => {
  const { campaignId } = useParams();
  const lang = useLanguage((s) => s.language);
  const programData = useCampaignStore((s) => s.programData);
  const oldProgramData = useCampaignStore((s) => s.oldProgramData);
  const setProgramData = useCampaignStore((s) => s.setProgramData);
  const setTimeStart = useCampaignStore((s) => s.setTimeStart);
  const setLoadingPage = useFlowStore((s) => s.setLoadingPage);
  const nodes = useFlowStore((s) => s.nodes);
  const initialId = useFlowStore((s) => s.initialId);

  const isMulti = useMemo(() => {
    return nodes.find(
      (x) => x?.data?.source === initialId && x?.data?.actionType === "event",
    );
  }, [nodes, initialId]);

  const isReprog = useMemo(() => {
    let params = new URLSearchParams(location.search);
    let reprog = params.get("reprogram");

    return !!reprog;
  }, []);

  const isPast = useMemo(() => {
    if (!oldProgramData) {
      return false;
    }

    return isReprog
      ? false
      : dayjs(oldProgramData?.date_start).diff(dayjs()) < 0;
  }, [isReprog, oldProgramData]);

  const sharing = useFlowStore((s) => s.sharing);
  const setSharing = useFlowStore((s) => s.setSharing);

  const sharingForm = useForm({
    defaultValues: {
      sharing: sharing?.map((x) => ({
        ...x,
        sharing_targets: x.sharing_targets.map((sharing_target) => ({
          ...sharing_target,
          option_name: sharing_target.name,
        })),
        ids_list: x.sharing_targets.map((x) => x.id),
      })),
    },
  });

  const navigate = useNavigate();
  const handleProgram = useCallback(
    (formData) => {
      const workflowProfile = useFlowStore.getState().profile;
      const sharing = sharingForm
        .getValues()
        .sharing.filter((x) => x.sharing_targets.length > 0)
        .map((share) => ({
          ...share,
          ids_list: share.sharing_targets.map((x) => x.id),
          sharing_targets: share.sharing_targets,
        }));
      const statsShare = useFlowStore.getState().shareStats;

      let prog_sharing =
        sharing[0]?.ids_list.length > 0 && statsShare.read_permission
          ? [
              {
                ...sharing[0],
                name: workflowProfile.name,
                read_permission: true,
                write_permission: false,
                delete_permission: false,
              },
            ]
          : [];

      let finalData = { ...programData, ...formData };

      // const needTimezone = [
      //   "time_start_sending",
      //   "time_stop_sending",
      // ];

      // let timezoneOffset = getTimezoneOffset(programData?.time_sending_timezone)

      // needTimezone.forEach((element) => {
      //   finalData[element] = incrementHour(
      //     finalData[element],
      //     timezoneOffset
      //   );
      // });

      finalData.stats_sharings = prog_sharing;

      let oldData = { ...oldProgramData };
      finalData.ab_proportion = oldData?.ab_proportion
        ? oldData.ab_proportion
        : finalData.ab_proportion || 0;

      finalData.time_sending_timezone =
        oldData?.time_sending_timezone ||
        useProfileStore.getState().profile?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      Object.keys(finalData).forEach((key) => {
        if (
          (key.includes("time_start_sending_") ||
            key.includes("time_stop_sending_")) &&
          finalData[key] &&
          finalData[key]?.length < 7
        ) {
          finalData[key] = finalData[key] + ":00";
        }
      });

      const diff = getObjectWithChangedProperties(oldData, finalData);

      if (isPast) {
        delete diff.date_start;
        delete diff.isPast;
      }

      function program() {
        setLoadingPage(true);
        const main = campaignId && !isReprog ? editCampaign : programWorkflow;
        main({
          workflow_id: workflowProfile?.id,
          data: campaignId && !isReprog ? diff : finalData,
          programmation_id: parseInt(campaignId),
        })
          .then((response) => {
            let newProgramId =
              response?.data?.workflow_programmation_id || parseInt(campaignId);

            notifications.show({
              message:
                campaignId && !isReprog
                  ? lang.campaign.edited
                  : lang.campaign.programmed,
              color: "var(--mantine-color-green-8)",
            });

            const setWorkflowProfile = useFlowStore.getState().setProfile;
            const setNodes = useFlowStore.getState().setNodes;
            const setEdges = useFlowStore.getState().setEdges;
            setWorkflowProfile({
              id: null,
              name: "",
              tags: [],
              folder: null,
              sharing: [],
              auto_remove_responders: true,
            });
            setNodes([]);
            setEdges([]);
            setProgramData(defaultProgramData);

            // Handle Sharing PRM
            const prmShare = useFlowStore.getState().sharePrm;

            if (prmShare.read_permission && sharing[0]?.ids_list.length > 0) {
              let found = useLeadsStore
                .getState()
                .sharings.find((x) => x.name === workflowProfile?.name);

              const mainSharer = found ? editSharing : createSharing;
              mainSharer({
                id: found?.id,
                ...sharing[0],
                name: workflowProfile?.name,
                read_permission: true,
                delete_permission: prmShare.delete_permission,
                write_permission: prmShare.write_permission,
                filter: {
                  mode: "and",
                  values: [
                    {
                      field_name: "programmation_id",
                      type: "equals",
                      value: newProgramId.toString(),
                    },
                  ],
                },
              })
                .then((res) => {
                  notifications.show({ message: lang.prm.sharing.shared });
                  navigate(
                    "/statistics/campaigns/running?highlight=" + newProgramId,
                  );
                })
                .finally(() => {
                  setLoadingPage(false);
                });
            } else {
              navigate(
                "/statistics/campaigns/running?highlight=" + newProgramId,
              );
            }
          })
          .catch(() => setLoadingPage(false));
      }

      const lists = useListStore.getState().lists;
      let listHasJob = programData.contactlist_ids.some((id) => {
        let list = lists.find((x) => x.id === id);

        if (list?.state === "InProgress" || list?.state === "InQueue") {
          return true;
        }
        return false;
      });

      if (listHasJob) {
        return modals.openConfirmModal({
          title: lang.campaign.alert_finish.title,
          children: <Text size="sm">{lang.campaign.alert_finish.text}</Text>,
          centered: true,
          labels: {
            confirm: lang.model_emails.program,
            cancel: lang.global.cancel,
          },
          onConfirm: () => {
            program();
          },
        });
      }

      if (Object.keys(diff).length === 0) {
        notifications.show({
          message:
            campaignId && !isReprog
              ? lang.campaign.edited
              : lang.campaign.programmed,
          color: "var(--mantine-color-green-8)",
        });

        return navigate(
          "/statistics/campaigns/running?highlight=" + campaignId,
        );
        // return modals.open({
        //   title: lang.campaign.no_edition.title,
        //   children: (
        //     <Stack>
        //       <Text size="sm">{lang.campaign.no_edition.text}</Text>
        //       <Text size="sm">{lang.campaign.no_edition.text2}</Text>
        //       <Group justify="end" mt={"md"}>
        //         <BackButton onClick={() => modals.closeAll()} />

        //         <Button
        //           onClick={() => {
        //             navigate(
        //               "/statistics/campaigns/running?highlight=" + campaignId
        //             );
        //             modals.closeAll();
        //           }}
        //         >
        //           {lang.campaign.no_edition.to_campaign}
        //         </Button>
        //       </Group>
        //     </Stack>
        //   ),
        //   centered: true,
        // });
      }

      return program();
    },
    [
      programData,
      campaignId,
      isPast,
      isReprog,
      lang,
      navigate,
      setLoadingPage,
      setProgramData,
      oldProgramData,
    ],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    let fd = new FormData(e.target);
    const data = Object.fromEntries(fd);
    // data.allowed_monday = data.allowed_monday ? true : false;
    // data.allowed_tuesday = data.allowed_tuesday ? true : false;
    // data.allowed_wednesday = data.allowed_wednesday ? true : false;
    // data.allowed_thursday = data.allowed_thursday ? true : false;
    // data.allowed_friday = data.allowed_friday ? true : false;
    // data.allowed_saturday = data.allowed_saturday ? true : false;
    // data.allowed_sunday = data.allowed_sunday ? true : false;
    data.priority_email = data.priority_email ? true : false;
    data.resend_to_bounce = data.resend_to_bounce ? true : false;
    data.stop_steps_at_end_date = data.stop_steps_at_end_date ? true : false;
    data.daily_send_limit = parseInt(data?.daily_send_limit || "0");
    data.limit_total_sending = parseInt(data?.limit_total_sending || "0");
    data.ab_proportion = parseInt(data?.ab_proportion) || undefined;
    data.date_stop = data?.date_stop ? data.date_stop : null;

    Object.keys(data).forEach((key) => {
      if (
        key.includes("time_start_sending_") ||
        key.includes("time_stop_sending_")
      ) {
        if (!data[key]) {
          data[key] = null;
        }
      }
    });

    handleProgram(data);
    setProgramData({ ...programData, ...data });
  };

  // const handleChange = (e) => {
  //   const formElement = e.target.form;

  //   // Petite sécurité au cas où le champ ne serait pas dans un formulaire
  //   if (!formElement) return;
  //   let fd = new FormData(formElement);
  //   const data = Object.fromEntries(fd);
  //   // data.allowed_monday = data.allowed_monday ? true : false;
  //   // data.allowed_tuesday = data.allowed_tuesday ? true : false;
  //   // data.allowed_wednesday = data.allowed_wednesday ? true : false;
  //   // data.allowed_thursday = data.allowed_thursday ? true : false;
  //   // data.allowed_friday = data.allowed_friday ? true : false;
  //   // data.allowed_saturday = data.allowed_saturday ? true : false;
  //   // data.allowed_sunday = data.allowed_sunday ? true : false;
  //   data.priority_email = data.priority_email ? true : false;
  //   data.resend_to_bounce = data.resend_to_bounce ? true : false;
  //   data.stop_steps_at_end_date = data.stop_steps_at_end_date ? true : false;
  //   data.daily_send_limit = parseInt(data?.daily_send_limit || "0");
  //   data.limit_total_sending = parseInt(data?.limit_total_sending || "0");
  //   data.ab_proportion = parseInt(data?.ab_proportion) || undefined;
  //   data.date_stop = data?.date_stop ? data.date_stop : null;

  //   Object.keys(data).forEach((key) => {
  //     if (
  //       key.includes("time_start_sending_") ||
  //       key.includes("time_stop_sending_")
  //     ) {
  //       if (!data[key]) {
  //         data[key] = null;
  //       }
  //     }
  //   });

  //   setProgramData({ ...programData, ...data });
  // };

  const [accordionValue, setAccordionValue] = useState(null);
  const viewportRef = useRef(null); // Pour le Paper
  const targetRef = useRef(null); // Pour l'élément à atteindre

  const onAdvancedClick = () => {
    // 1. Ouvrir l'accordéon
    setAccordionValue("advanced");

    // 2. Attendre le rendu du contenu
    setTimeout(() => {
      if (viewportRef.current && targetRef.current) {
        const parent = viewportRef.current;
        const child = targetRef.current;

        // Calcul de la position relative du scroll
        const relativeTop = child.offsetTop - parent.offsetTop;

        // Scroll fluide à l'intérieur du container
        parent.scrollTo({
          top: relativeTop - 20, // -20px pour laisser une petite marge en haut
          behavior: "smooth",
        });
      }
    }, 150); // Un délai légèrement plus long pour laisser l'animation Mantine commencer
  };

  useEffect(() => {
    const sh = sharingForm
      .getValues()
      .sharing.filter((x) => x.sharing_targets.length > 0)
      .map((share) => ({
        ...share,
        ids_list: share.sharing_targets.map((x) => x.id),
        sharing_targets: share.sharing_targets,
      }));

    setSharing(sh);
  }, [setSharing, sharingForm]);

  return (
    <Stack
      w={"85%"}
      mx={"auto"}
      gap={"md"}
      flex={1}
      component={"form"}
      onSubmit={handleSubmit}
      // onChange={handleChange}
    >
      <Group mt={"xl"}>
        <Text fw={700} fz={24}>
          {lang.campaign.programmation_title}
        </Text>
      </Group>

      <Stack h={"calc(100vh - 162px)"}>
        <Paper
          ref={viewportRef}
          withBorder
          radius={16}
          p={"lg"}
          flex={"1 1 auto"}
          h={0}
          component={Stack}
          gap={"xl"}
          style={{ boxShadow: "#00000012 0 2px 6px", overflow: "auto" }}
        >
          <Group wrap="nowrap" justify="space-between" align="start">
            <Tooltip
              label={lang.campaign.already_programmed}
              disabled={!isPast}
            >
              <DateTimePicker
                dropdownType="modal"
                label={lang.workflows.program.step_config.date_start}
                defaultValue={
                  isReprog
                    ? new Date()
                    : programData?.date_start
                      ? new Date(programData.date_start)
                      : new Date()
                }
                leftSection={<IconCalendarTime />}
                rightSectionWidth={"auto"}
                rightSection={<ProfileTimezone disabled={isPast} />}
                w={360}
                name="date_start"
                valueFormat="DD MMM YYYY à HH:mm A"
                size="md"
                radius={"md"}
                required
                disabled={isPast}
              />
            </Tooltip>

            <TimeInput
              label={lang.workflows.program.step_config.time_start_sending}
              name="time_start_sending"
              onChange={(e) => setTimeStart(e.target.value)}
              size="md"
              required
              defaultValue={
                useCampaignStore.getState().timeStart
                  ? useCampaignStore.getState().timeStart
                  : programData?.time_start_sending
                    ? programData.time_start_sending
                    : defaultProgramData.time_start_sending
              }
              leftSection={<IconClock />}
              rightSection={
                <Group>
                  <ProfileTimezone
                    timezone={
                      programData?.time_sending_timezone ||
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    }
                  />

                  <HoverCard position="top" withArrow>
                    <HoverCard.Target>
                      <ThemeIcon size={"lg"}>
                        <IconHelp />
                      </ThemeIcon>
                    </HoverCard.Target>

                    <HoverCard.Dropdown>
                      <Text size="sm" w={200}>
                        {
                          lang.workflows.program.step_config
                            .time_start_sending_help
                        }
                      </Text>
                    </HoverCard.Dropdown>
                  </HoverCard>
                </Group>
              }
              rightSectionWidth={"auto"}
              radius={"md"}
              w={350}
              withSeconds
            />

            {isMulti ? (
              <Text w={300} fw={500} mt={16}>
                {lang.workflows.program.step_config.more.multi_limit_warning}
              </Text>
            ) : (
              <NumberInput
                label={lang.workflows.program.step_config.daily_send_limit}
                name="daily_send_limit"
                defaultValue={
                  programData?.daily_send_limit
                    ? programData.daily_send_limit
                    : defaultProgramData.daily_send_limit
                }
                min={0}
                size="md"
                w={300}
                leftSection={<IconWiper />}
                rightSectionWidth={50}
                rightSection={
                  <Tooltip label={lang.campaign.limit_help}>
                    <ThemeIcon size={"lg"}>
                      <IconHelp />
                    </ThemeIcon>
                  </Tooltip>
                }
              />
            )}

            <Button
              mt={24}
              variant="light"
              leftSection={<IconSettings size={20} />}
              size="md"
              rightSection={<IconArrowDown size={16} />}
              onClick={onAdvancedClick}
            >
              {lang.campaign.nodes.advanced_settings.label}
            </Button>
          </Group>

          <Accordion variant="filled" defaultValue={"exclusion"}>
            <Accordion.Item value={"exclusion"}>
              <Accordion.Control
                bg={"var(--mantine-color-gray-1)"}
                icon={<IconSendOff size={16} />}
                px={"xs"}
                style={{ borderRadius: 12 }}
              >
                {lang.workflows.program.step_config.exclusion}
              </Accordion.Control>
              <Accordion.Panel>
                <CampaignExclusion />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Accordion
            variant="filled"
            value={accordionValue}
            onChange={setAccordionValue}
          >
            <Accordion.Item value="advanced" ref={targetRef}>
              <Accordion.Control
                id="advanced"
                bg={"var(--mantine-color-gray-1)"}
                icon={<IconSettings size={16} />}
                px={"xs"}
                style={{ borderRadius: 12 }}
              >
                {lang.campaign.nodes.advanced_settings.label}
              </Accordion.Control>
              <Accordion.Panel>
                <CampaignAdvancedSettings
                  programData={programData}
                  isPast={isPast}
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <Accordion variant="filled">
            <Accordion.Item value={"sharing"}>
              <Accordion.Control
                icon={<IconShare size={16} />}
                px={"xs"}
                bg={"var(--mantine-color-gray-1)"}
                style={{ borderRadius: 12 }}
              >
                {lang.components.sharing.title}
              </Accordion.Control>
              <Accordion.Panel pt={4}>
                <SharingController
                  form={sharingForm}
                  max={1}
                  disablePermissions={true}
                  disableDelete
                  size="md"
                />
                <CampaignSharer />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Paper>
      </Stack>

      <button
        style={{ display: "none" }}
        type="submit"
        id="programmation-button"
      >
        Hey
      </button>
    </Stack>
  );
};

export default ProgrammationStep;
